'use client'

import React, { SyntheticEvent, useEffect, useRef, useState, useId } from 'react'
import type { ActionMeta, MultiValue, Options, SingleValue } from 'react-select'
import Select, { SelectInstance, SelectComponentsConfig, Props as StateManagerProps } from 'react-select'
import { useInputWrapper } from '../../../hooks/useInputWrapper'
import {
    ClearIndicatorReplacement,
    DropdownIndicatorReplacement,
    MultiLineOption,
    SingleValueMultiLineComponent,
    SingleValueTagComponent,
} from './Components'
import { OptionReplacement } from './Components/OptionReplacement'
import {
    SelectOption,
    GroupedSelectOption,
    SingleSelectInputProps,
    NewSingleSelectInputProps,
} from '../../../types/SelectInput'
import {
    getSelectInputClassNames,
    getSelectContainerClassNames,
    getCustomSelectInputStyles,
    getFilterOption,
    FilterOptionOption,
} from './Helpers/select-helpers'
import { Text } from '../../atoms'

const getSelectProps = ({
    disabled = false,
    readOnly = false,
    isSearchable = false,
    showAsTag = false,
    showMultiLine = false,
    id,
    onBlur,
    isInline = false,
    placeholder,
    noOptionsMessage = 'No results',
    autoFocus = false,
    isOptionDisabled: isOptionDisabledProp,
}: SingleSelectInputProps) => {
    const { onFocusHandler, onBlurHandler, classes } = useInputWrapper(false, disabled)
    const randomId = useId()
    const selectId = id ?? randomId

    const components: SelectComponentsConfig<SelectOption, boolean, GroupedSelectOption> = {
        DropdownIndicator: DropdownIndicatorReplacement,
        ClearIndicator: ClearIndicatorReplacement,
        ...(!showMultiLine && !showAsTag && { Option: OptionReplacement }),
    }

    if (showAsTag) components.SingleValue = SingleValueTagComponent
    if (showMultiLine) components.SingleValue = SingleValueMultiLineComponent

    const selectProps: StateManagerProps<SelectOption, boolean, GroupedSelectOption> = {
        instanceId: selectId,
        id: selectId,
        inputId: selectId,
        className: getSelectInputClassNames({ isInline, isMulti: false, isSearchable, showAsTag, readOnly }),
        classNamePrefix: 'select-input',
        isDisabled: disabled,
        closeMenuOnSelect: true,
        hideSelectedOptions: false,
        isSearchable: !readOnly ? isSearchable : false,
        onBlur: (event: SyntheticEvent) => {
            if (onBlur) onBlur(event)
            onBlurHandler()
        },
        menuPlacement: 'auto',
        noOptionsMessage: () => <Text>{noOptionsMessage}</Text>,
        components,
        styles: getCustomSelectInputStyles(showMultiLine, isSearchable),
        filterOption: (option: FilterOptionOption<SelectOption>, input: string) => getFilterOption(option, input, showMultiLine),
        ...(showMultiLine && { formatOptionLabel: MultiLineOption }),
        ...(readOnly && { menuIsOpen: false }),
        ...(placeholder ? { placeholder } : { placeholder: '' }),
        autoFocus,
        isOptionDisabled:
            isOptionDisabledProp != null
                ? (option: unknown, selectValue: unknown) =>
                      isOptionDisabledProp(option as SelectOption, selectValue as Options<SelectOption>)
                : (option: unknown, _selectValue: unknown) => !!(option as SelectOption).disabled,
    }

    return {
        selectProps,
        onFocusHandler,
        classes,
    }
}

/**
 * This version of SingleSelectInput works in Admin, but not Account.
 */
export const SingleSelectInput = ({
    options = [],
    disabled = false,
    readOnly = false,
    isSearchable = false,
    showAsTag = false,
    showMultiLine = false,
    id,
    onChange,
    onBlur,
    value,
    defaultValue,
    isInline = false,
    className = '',
    placeholder,
    noOptionsMessage = 'No results',
    autoFocus = false,
    isClearable = false,
    isLoading,
    menuPosition = 'fixed',
    /** @todo This prop needs to be provided in all use-cases if applicable for accessibility */
    hasError = false,
    isOptionDisabled: isOptionDisabledFromProps,
    ...props
}: SingleSelectInputProps) => {
    const { classes, selectProps, onFocusHandler } = getSelectProps({
        options,
        disabled,
        readOnly,
        isSearchable,
        showAsTag,
        showMultiLine,
        id,
        onChange,
        onBlur,
        value,
        defaultValue,
        isInline,
        className,
        placeholder,
        noOptionsMessage,
        autoFocus,
        isClearable,
        isLoading,
        menuPosition,
        hasError,
        isOptionDisabled: isOptionDisabledFromProps ?? ((option: SelectOption) => !!option.disabled),
    })

    return (
        <div
            className={getSelectContainerClassNames(isInline, classes, className)}
            data-cy="input-select"
            data-testid={props['data-testid'] ?? 'select-input'}
        >
            <Select
                {...selectProps}
                defaultValue={defaultValue}
                isClearable={!readOnly && isClearable}
                options={options}
                value={options.filter((option) => value === option.value)}
                onFocus={onFocusHandler}
                onChange={(newValue: MultiValue<SelectOption> | SingleValue<SelectOption>, actionMeta: ActionMeta<SelectOption>) => {
                    if (!onChange) return
                    if (newValue == null || (Array.isArray(newValue) && newValue.length === 0)) {
                        onChange(newValue as unknown as SelectOption, actionMeta)
                        return
                    }
                    const selected = Array.isArray(newValue) ? newValue[0] : newValue
                    if (selected) {
                        onChange(selected, actionMeta)
                    }
                }}
                openMenuOnClick={!readOnly}
                isLoading={isLoading}
                menuPosition={menuPosition}
                aria-invalid={hasError}
            />
        </div>
    )
}

/**
 * @description
 * This version of SingleSelectInput works in Account, but not Admin. This is a 'quick fix' and eventually all projects should be able to use the same component.
 * How this component differs from SingleSelectInput: 'value' no longer gets passed to Select, defaultValue is determined from options, the 'name' property gets passed to Select.
 */
export const NewSingleSelectInput = ({
    options = [],
    disabled = false,
    readOnly = false,
    isSearchable = false,
    showAsTag = false,
    showMultiLine = false,
    id,
    onChange,
    onBlur,
    value,
    defaultValue,
    isInline = false,
    className = '',
    placeholder,
    noOptionsMessage = 'No results',
    autoFocus = false,
    isClearable = false,
    isLoading,
    menuPosition = 'fixed',
    /** @todo This prop needs to be provided in all use-cases if applicable for accessibility */
    hasError = false,
    name,
    required = false,
    isOptionDisabled: isOptionDisabledFromProps,
    ...props
}: NewSingleSelectInputProps) => {
    const { classes, selectProps, onFocusHandler } = getSelectProps({
        options,
        disabled,
        readOnly,
        isSearchable,
        showAsTag,
        showMultiLine,
        id,
        onChange,
        onBlur,
        value,
        defaultValue,
        isInline,
        className,
        placeholder,
        noOptionsMessage,
        autoFocus,
        isClearable,
        isLoading,
        menuPosition,
        hasError,
        isOptionDisabled: isOptionDisabledFromProps ?? ((option: SelectOption) => !!option.disabled),
    })
    const componentRef = useRef<SelectInstance<SelectOption>>(null)
    const selectRef = props.selectInputRef ?? componentRef
    const hiddenInputRef = useRef<HTMLInputElement>(null)

    // Track if the component has mounted to avoid unnecessary re-renders
    const hasMounted = useRef(false)

    // Generate a unique ID for this instance
    const instanceId = useRef(`select-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`)

    // Track the controlled value separately from defaultValue
    const [controlledValue, setControlledValue] = useState<string | number | null>(defaultValue)

    // Update the controlled value when defaultValue changes
    useEffect(() => {
        // Only update if this isn't the initial mount
        if (hasMounted.current) {
            setControlledValue(defaultValue)
        } else {
            hasMounted.current = true
        }
    }, [defaultValue])

    // Effect to handle programmatic clearing
    useEffect(() => {
        if (controlledValue === null && selectRef.current) {
            selectRef.current.clearValue()

            if (hiddenInputRef.current) {
                hiddenInputRef.current.value = ''
            }
        }
    }, [controlledValue])

    const handleChange = (
        newValue: MultiValue<SelectOption> | SingleValue<SelectOption>,
        actionMeta: ActionMeta<SelectOption>,
    ) => {
        if (newValue == null || (Array.isArray(newValue) && newValue.length === 0)) {
            setControlledValue(null)
            if (hiddenInputRef.current) {
                hiddenInputRef.current.value = ''
                const event = new Event('change', { bubbles: true })
                hiddenInputRef.current.dispatchEvent(event)
            }
            if (onChange) {
                onChange(newValue as unknown as SelectOption, actionMeta)
            }
            if (selectRef.current) {
                selectRef.current.blur()
            }
            return
        }
        const selected = Array.isArray(newValue) ? newValue[0] : newValue
        if (!selected) return

        setControlledValue(selected.value ?? null)

        if (hiddenInputRef.current) {
            hiddenInputRef.current.value = selected.value?.toString() || ''

            const event = new Event('change', { bubbles: true })
            hiddenInputRef.current.dispatchEvent(event)
        }

        if (onChange) {
            onChange(selected, actionMeta)
        }

        if (selectRef.current) {
            selectRef.current.blur()
        }
    }

    return (
        <div
            className={getSelectContainerClassNames(isInline, classes, className)}
            data-cy="input-select"
            data-testid={props['data-testid'] ?? 'select-input'}
            key={`${instanceId.current}-${isLoading ? 'loading' : 'normal'}`}
        >
            {/* Hidden input for form submission */}
            <input
                ref={hiddenInputRef}
                type="hidden"
                name={name}
                id={`${id || name}-hidden`}
                value={controlledValue?.toString() || ''}
            />
            <Select
                {...selectProps}
                ref={selectRef}
                key={`select-${controlledValue === null ? 'null' : controlledValue}`}
                defaultValue={options?.find((option) => controlledValue === option.value) || null}
                isClearable={!readOnly && isClearable}
                options={options}
                onFocus={onFocusHandler}
                onChange={handleChange}
                openMenuOnClick={!readOnly}
                isLoading={isLoading}
                menuPosition={menuPosition}
                aria-invalid={hasError}
                name={name}
                required={required}
            />
        </div>
    )
}
